import { PartQuantity, SupplierPart } from "./partsCalculationUtils";

export type NestedStockPart = Record<string, unknown> & {
  partNumber: string;
  quantity: number;
  binLocation: string;
};

export type JoinedSupplierPartRow = {
  partId: string;
  partNumber: string;
  rootPartNumber: string;
  partName?: string;
  askedQty: number;
  /** Net available quantity (Typesense family qty minus delta / part-confirmation). */
  stockQty: number;
  /**
   * The supplier's raw advertised stock — the "supplier reported" number, before any
   * delta / part-confirmation subtraction. This is what should be stamped into a
   * part-confirmation request (the backend needs raw stock, not net available).
   */
  reportedStockQty: number;
  binLocation: string;
  updatedAt?: number;
  parts: NestedStockPart[];
  alternateParts: NestedStockPart[];
  supplierPart: SupplierPart & { id?: string };
};

const BIN_KEYS = ["binLocation", "bin", "binNo", "bin_no", "binNumber"];

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

export const normalizeNestedStockParts = (raw: unknown): NestedStockPart[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((item) => {
      const record = asRecord(item);
      if (!record) {
        return null;
      }
      const partNumber = String(record.partNumber ?? "").trim();
      if (!partNumber) {
        return null;
      }
      const binLocation = BIN_KEYS.map((key) => String(record[key] ?? "").trim()).find(
        Boolean,
      ) ?? "";
      return {
        ...record,
        partNumber,
        quantity: Number(record.quantity) || 0,
        binLocation,
      };
    })
    .filter((part): part is NestedStockPart => part != null);
};

const nestedStockPartsFromHit = (
  hit: unknown,
  field: "parts" | "alternateParts",
): NestedStockPart[] => {
  const record = asRecord(hit);
  if (!record) {
    return [];
  }
  const document = asRecord(record.document);
  return normalizeNestedStockParts(record[field] ?? document?.[field]);
};

const binFromRecord = (record: Record<string, unknown> | null): string => {
  if (!record) {
    return "";
  }
  return BIN_KEYS.map((key) => String(record[key] ?? "").trim()).find(Boolean) ?? "";
};

export const resolveOrderedPartBinLocation = (
  partNumber: string,
  familyParts: NestedStockPart[],
  alternateParts: NestedStockPart[],
  hit?: unknown,
): string => {
  const record = asRecord(hit);
  const document = asRecord(record?.document);
  const hitBin = binFromRecord(record) || binFromRecord(document);
  if (hitBin) {
    return hitBin;
  }

  const ordered = String(partNumber ?? "").trim().toLowerCase();
  const nested = [...familyParts, ...alternateParts];
  const exact = nested.find(
    (part) =>
      part.partNumber.trim().toLowerCase() === ordered && part.binLocation,
  );
  if (exact?.binLocation) {
    return exact.binLocation;
  }

  const inStock = nested
    .filter((part) => part.quantity > 0 && part.binLocation)
    .sort((a, b) => b.quantity - a.quantity);
  return inStock[0]?.binLocation ?? "";
};

export const joinSupplierPartRows = (
  remainingParts: PartQuantity[],
  supplierParts: Array<SupplierPart & { id?: string }>,
): JoinedSupplierPartRow[] => {
  const supplierPartsMap = new Map(
    supplierParts.map((part) => [part.rootPartNumber, part]),
  );

  return remainingParts.flatMap((orderPart) => {
    const matchingPart = supplierPartsMap.get(orderPart.rootPartNumber);
    if (!matchingPart) {
      return [];
    }

    const partId =
      orderPart.partId ||
      (matchingPart as { id?: string }).id ||
      orderPart.rootPartNumber;

    const familyParts = nestedStockPartsFromHit(matchingPart, "parts");
    const alternateParts = nestedStockPartsFromHit(
      matchingPart,
      "alternateParts",
    );

    return [
      {
        partId,
        partNumber: orderPart.partNumber,
        rootPartNumber: orderPart.rootPartNumber,
        partName: orderPart.partName,
        askedQty: orderPart.quantity,
        stockQty: matchingPart.familyQuantity,
        reportedStockQty:
          matchingPart.supplierReportedQuantity ?? matchingPart.familyQuantity,
        binLocation: resolveOrderedPartBinLocation(
          orderPart.partNumber,
          familyParts,
          alternateParts,
          matchingPart,
        ),
        updatedAt: matchingPart.updatedAt,
        parts: familyParts,
        alternateParts,
        supplierPart: matchingPart,
      },
    ];
  });
};
